/**
 * Canonical challenge duration-unit values (the stored `price.durationUnit`).
 *
 * The value IS the stored/wire representation, shared with the launchpad, mobile
 * and system2-server, so these string values must never change.
 *
 * An absent unit means 'month', which keeps every pre-existing contract working
 * without a migration.
 */
const DURATION_UNIT = {
  WEEK: 'week',
  MONTH: 'month',
};

/**
 * Maps a duration unit to its moment.js duration key. Absent/unknown unit means
 * 'months', preserving pre-existing month-based behavior.
 *
 * @param {'week'|'month'} [durationUnit] The stored `price.durationUnit`.
 * @returns {'weeks'|'months'} The moment.js duration key.
 */
const toMomentUnit = (durationUnit) => (durationUnit === DURATION_UNIT.WEEK ? 'weeks' : 'months');

/**
 * The unit alone, pluralized for the count, e.g. `weeks` / `month`.
 *
 * @param {number} count The term count, interpreted in `durationUnit`.
 * @param {'week'|'month'} [durationUnit] The stored `price.durationUnit`.
 * @returns {string} The pluralized unit.
 */
const formatPluralUnit = (count, durationUnit) => {
  const unit = durationUnit === DURATION_UNIT.WEEK ? DURATION_UNIT.WEEK : DURATION_UNIT.MONTH;
  return `${unit}${count === 1 ? '' : 's'}`;
};

/**
 * Renders a term count with its pluralized unit, e.g. `6 weeks` / `1 month`.
 * Matches the mobile app's `formatPluralDuration` so both clients read the same.
 *
 * @param {number} count The term count, interpreted in `durationUnit`.
 * @param {'week'|'month'} [durationUnit] The stored `price.durationUnit`.
 * @returns {string} The formatted duration.
 */
const formatPluralDuration = (count, durationUnit) => `${count} ${formatPluralUnit(count, durationUnit)}`;

export {
  DURATION_UNIT,
  toMomentUnit,
  formatPluralUnit,
  formatPluralDuration,
};
